import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';

const QuotesSlider: React.FC = () => {
    const { getArray } = useLanguage();
    const quotes = getArray('quotes');
    const [index, setIndex] = useState(0);

    const next = () => setIndex((prev) => (prev + 1) % quotes.length);
    const prev = () => setIndex((prev) => (prev - 1 + quotes.length) % quotes.length);

    if (!quotes.length) return null;

    return (
        <div className="px-6 py-6 flex flex-col items-center">
            <div className="max-w-4xl w-full relative">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={index}
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 1.1, y: -20 }}
                        className="bg-white dark:bg-romantic-cardDark p-8 md:p-12 rounded-[2.5rem] shadow-2xl border border-primary-red/10 relative overflow-hidden flex flex-col items-center"
                    >
                        <div className="absolute top-6 left-8 text-primary-red/20">
                            <Quote size={48} />
                        </div>
                        <div className="absolute bottom-6 right-8 text-primary-red/20 rotate-180">
                            <Quote size={48} />
                        </div>

                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.2 }}
                            className="text-lg md:text-xl font-playfair font-bold text-gray-800 dark:text-white text-center leading-relaxed italic z-10"
                        >
                            "{quotes[index].text}"
                        </motion.p>

                        <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: "3rem" }}
                            className="h-1 bg-primary-red mt-6 rounded-full z-10"
                        />

                        <p className="mt-4 text-primary-red font-poppins font-bold text-xs uppercase tracking-widest z-10">
                            Quote {index + 1} of {quotes.length}
                        </p>
                    </motion.div>
                </AnimatePresence>

                {/* Navigation Buttons */}
                <div className="absolute top-1/2 -left-4 md:-left-8 -translate-y-1/2">
                    <button
                        onClick={prev}
                        className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-white dark:bg-gray-800 shadow-xl border border-gray-100 dark:border-gray-700 flex items-center justify-center text-primary-red hover:bg-primary-red hover:text-white transition-all transform active:scale-95"
                    >
                        <ChevronLeft size={20} />
                    </button>
                </div>
                <div className="absolute top-1/2 -right-4 md:-right-8 -translate-y-1/2">
                    <button
                        onClick={next}
                        className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-white dark:bg-gray-800 shadow-xl border border-gray-100 dark:border-gray-700 flex items-center justify-center text-primary-red hover:bg-primary-red hover:text-white transition-all transform active:scale-95"
                    >
                        <ChevronRight size={20} />
                    </button>
                </div>

                {/* Dots */}
                <div className="flex space-x-2 mt-6 max-w-full overflow-x-auto p-2 scrollbar-none justify-center">
                    {quotes.map((_, i) => (
                        <button
                            key={i}
                            onClick={() => setIndex(i)}
                            className={`h-2 rounded-full transition-all duration-300 ${i === index ? 'bg-primary-red w-6' : 'bg-romantic-pink/40 w-2'
                                }`}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default QuotesSlider;
